import { SuccessfullAddRisqueDialog } from '../components/risque/successfullAddRisqueDialog';
import { AddRisqueDialog } from '../components/risque/addRisqueDialog';
import RisquePageHeader from '../components/risque/risquePageHeader';
import RisqueFiltration from '../components/risque/risqueFiltration';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { useHasPermission } from '../hooks/useHasPermission';
import { Divider, Box, LinearProgress } from '@mui/material';
import RisqueList from '../components/risque/risqueList';
import RisqueGrid from '../components/risque/risqueGrid';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';
import '../css/roles/roles.css';

const Risque = () => {

    const hasRisqueCreatePermission = useHasPermission('risk_create');

    const [openAddRisque, setOpenAddRisque] = useState(false);
    const [openSuccessfulAddRisque, setOpenSuccessfulAddRisque] = useState(false);

    const [loading, setLoading] = useState(true);
    const [risques, setRisques] = useState([])
    const [totalRisques, setTotalRisques] = useState('');
    const [filteredRisquesCount, setFilteredRisquesCount] = useState('');
    const [risquesFetched, setRisquesFetched] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('selectedRisquesDisplayMode') || localStorage.getItem('selectedBrandsDisplayMode')) || 'grid');
    const [risquesFilters, setRisquesFilters] = useState(
        JSON.parse(localStorage.getItem('risquesFilters')) ||
        { debouncedSearch: '' }
    );
    const defaultSortBy = {
        name: null,
        description: null,
    };
    
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('risqueSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });
    
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'risques',
        pathname: '/dashboard/risques',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: risquesFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(risquesFetched);

    useEffect(() => {
        setLoading(true)
    }, [risquesFilters]);

    useEffect(() => {
        localStorage.setItem('risquesFilters', JSON.stringify(risquesFilters));
    }, [risquesFilters]);

    useEffect(() => {
        localStorage.setItem('risqueSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('selectedRisquesDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    const fetchRisques = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('risk/list', { params: { offset: adjustedOffset, limit, filters: risquesFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedRisques = [];
                // Update the fetched records in their correct positions
                response?.data?.risks?.forEach((risque, index) => { updatedRisques[adjustedOffset + index] = risque });
                // Update the state with the new metiers array
                setRisques(updatedRisques);
                setFilteredRisquesCount(response?.data?.filteredRisksCount);
                setTotalRisques(response?.data?.totalRisks);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching risques:', error);
            CustomErrorLogger(error, 'fetch risques in risques page');
        }
    }, [risquesFilters, offset, sortBy]);

    useEffect(() => {
        fetchRisques()
    }, [fetchRisques]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Risques" list={true}>
            <RisquePageHeader
                setOpenAddRisque={setOpenAddRisque}
                hasRisqueCreatePermission={hasRisqueCreatePermission}
            />
            <Divider />

            {loading ?
                <Box>
                    <RisqueFiltration
                        filtersEmpty={filtersEmpty}
                        risquesFilters={risquesFilters}
                        setRisquesFilters={setRisquesFilters}
                        totalRisques={totalRisques}
                        risquesFetched={risquesFetched}
                        filteredRisquesCount={filteredRisquesCount}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalRisques === 0 ?
                    <NoRecord
                        description="Aucun risque ajouté pour le moment"
                        buttonTitle="Ajouter un nouveau risque"
                        handleOpenDialog={() => setOpenAddRisque(true)}
                        hasPermission={hasRisqueCreatePermission}
                    /> :
                    <Box>
                        <RisqueFiltration
                            filtersEmpty={filtersEmpty}
                            risquesFilters={risquesFilters}
                            setRisquesFilters={setRisquesFilters}
                            totalRisques={totalRisques}
                            risquesFetched={risquesFetched}
                            filteredRisquesCount={filteredRisquesCount}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <RisqueGrid
                                scrollRef={scrollRef}
                                risques={risques}
                                setOffset={setOffset}
                                filteredRisquesCount={filteredRisquesCount}
                                setRisquesFetched={setRisquesFetched}
                            />
                        ) : (
                            <RisqueList
                                scrollRef={scrollRef}
                                risques={risques}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredRisquesCount={filteredRisquesCount}
                                setRisquesFetched={setRisquesFetched}
                            />
                        )}

                    </Box>
            }

            {openAddRisque &&
                <AddRisqueDialog
                    setRisques={setRisques}
                    setTotalRisques={setTotalRisques}
                    setFilteredRisquesCount={setFilteredRisquesCount}
                    openAddRisque={openAddRisque}
                    setOpenAddRisque={setOpenAddRisque}
                />
            }

            {openSuccessfulAddRisque &&
                <SuccessfullAddRisqueDialog
                    openSuccessfulAddRisque={openSuccessfulAddRisque}
                    setOpenAddRisque={setOpenAddRisque}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Risque;